import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "react-redux";
import { store } from "appredux/store";
import ConfigProvider from "components/ConfigProvider";
import useResponsive from "hooks/useResponsive";
import { Suspense, useEffect, useMemo, useState } from "react";
import { RouterProvider } from "react-router";
import { generateRoutes } from "./routes";
import { GoogleOAuthProvider } from "@react-oauth/google";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
  BarController,
} from "chart.js";
import { MatrixController, MatrixElement } from "chartjs-chart-matrix";
import { Loader } from "components/UI";
import { MessageProvider } from "components/MessageProvider";
import SentryErrorBoundary from "components/SentryErrorBoundary";
import InstallPromptWrapper from "components/InstallPromptWrapper";

// create history
// const history = createBrowserHistory({ window });
// window.customHistory = history;
window.appReduxStore = store;

// Use query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

Chart.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Legend,
  Title,
  Tooltip,
  MatrixController,
  MatrixElement,
  TimeScale,
  BarController,
);

function App() {
  const { setLayoutView } = useResponsive();

  const routes = useMemo(() => generateRoutes({ queryClient }), []);

  // Set resonsive listener
  useEffect(() => {
    setLayoutView(true);
    window.addEventListener("resize", () => {
      setLayoutView();
    });

    return () => window.removeEventListener("resize", setLayoutView);
  }, [setLayoutView]);

  return (
    <>
      <Suspense fallback={<Loader />}>
        <RouterProvider router={routes} />
      </Suspense>
      <InstallPromptWrapper />
    </>
  );
}

function AppContainer() {
  const [ReactQueryDevtools, setReactQueryDevtools] = useState(null);
  useEffect(() => {
    if (
      import.meta.env.MODE !== "production" &&
      import.meta.env.VITE_ENABLE_RQ_DEVTOOLS === "true"
    ) {
      import("@tanstack/react-query-devtools").then((module) => {
        setReactQueryDevtools(() => module.ReactQueryDevtools);
      });
    }
  }, []);
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider>
          <MessageProvider>
            <GoogleOAuthProvider
              clientId={import.meta.env.VITE_APP_GOOGLE_CLIENT_ID}
            >
              <SentryErrorBoundary showDialog>
                <App />
              </SentryErrorBoundary>
            </GoogleOAuthProvider>
          </MessageProvider>
        </ConfigProvider>
        {ReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </Provider>
  );
}

export default AppContainer;
